import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DatePicker } from "@/components/cinch/DatePicker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/components/ui/rich-text-editor";

import {
  useCreateObjective,
  useGoalOptions,
  useQuarters,
  useUpdateObjective,
  type Objective,
  type ObjectiveStatus,
} from "../api";
import { STATUS_META, dateToIso, isoToDate } from "../constants";
import { UserPicker } from "./UserPicker";

interface Props {
  quarterId: string;
  objective?: Objective;
  onClose: () => void;
}

export function ObjectiveForm({ quarterId, objective, onClose }: Props) {
  const isEdit = !!objective;
  const create = useCreateObjective();
  const update = useUpdateObjective();
  const goals = useGoalOptions();
  const quarters = useQuarters();

  const [title, setTitle] = useState(objective?.title ?? "");
  const [ownerId, setOwnerId] = useState<string | null>(objective?.owner_id ?? null);
  // Kept as a string so the field can be cleared (empty) while editing; parsed
  // to an int on submit.
  const [priority, setPriority] = useState(String(objective?.priority ?? 1));
  const [status, setStatus] = useState<ObjectiveStatus>(objective?.status ?? "not_started");
  const [targetDate, setTargetDate] = useState(objective?.target_date ?? "");
  const [definition, setDefinition] = useState(objective?.definition_of_success ?? "");
  const [planUrl, setPlanUrl] = useState(objective?.plan_url ?? "");
  const [planBody, setPlanBody] = useState(objective?.plan_body ?? "");
  const [goalId, setGoalId] = useState<string | null>(objective?.goal_id ?? null);
  // Editable on edit so a misfiled objective can move quarters (e.g. promoted
  // from a planning session into the wrong one).
  const [objQuarterId, setObjQuarterId] = useState(objective?.quarter_id ?? quarterId);

  const pending = create.isPending || update.isPending;

  function submit() {
    const payload = {
      title: title.trim(),
      owner_id: ownerId,
      priority: Math.min(99, Math.max(1, parseInt(priority, 10) || 1)),
      status,
      target_date: targetDate || null,
      definition_of_success: definition || null,
      plan_url: planUrl.trim() || null,
      plan_body: planBody || null,
      goal_id: goalId,
    };
    if (!payload.title) return;
    if (isEdit) {
      update.mutate(
        { id: objective.id, payload: { ...payload, quarter_id: objQuarterId } },
        { onSuccess: onClose },
      );
    } else {
      create.mutate({ quarter_id: quarterId, ...payload }, { onSuccess: onClose });
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit objective" : "New objective"}</DialogTitle>
          <DialogDescription>
            Set the owner, priority, and what success looks like. Attach a plan after saving.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="obj-title">Title</Label>
            <Input
              id="obj-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. PLG — define milestones"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="obj-owner">Owner</Label>
              <UserPicker id="obj-owner" value={ownerId} onChange={setOwnerId} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="obj-priority">Priority</Label>
              <Input
                id="obj-priority"
                type="number"
                min={1}
                max={99}
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="obj-status">Status</Label>
              <select
                id="obj-status"
                className="h-9 rounded-xl border border-input bg-background px-2 text-sm"
                value={status}
                onChange={(e) => setStatus(e.target.value as ObjectiveStatus)}
              >
                {STATUS_META.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* The DatePicker renders its own floating label, so no external
              <Label> here (that caused a doubled/overlapping label). */}
          <div className="flex flex-col gap-1.5">
            <DatePicker
              id="obj-target"
              aria-label="Target date"
              value={isoToDate(targetDate)}
              onChange={(d) => setTargetDate(dateToIso(d) ?? "")}
            />
          </div>

          {isEdit ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="obj-quarter">Quarter</Label>
              <select
                id="obj-quarter"
                className="h-9 rounded-xl border border-input bg-background px-2 text-sm"
                value={objQuarterId}
                onChange={(e) => setObjQuarterId(e.target.value)}
              >
                {(quarters.data ?? []).map((q) => (
                  <option key={q.id} value={q.id} disabled={q.is_closed && q.id !== objective?.quarter_id}>
                    {q.label}
                    {q.is_closed ? " (closed)" : ""}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="obj-goal">Company goal (optional)</Label>
            <select
              id="obj-goal"
              className="h-9 rounded-xl border border-input bg-background px-2 text-sm"
              value={goalId ?? ""}
              onChange={(e) => setGoalId(e.target.value || null)}
            >
              <option value="">— None —</option>
              {(goals.data ?? []).map((g) => (
                <option key={g.id} value={g.id}>
                  {g.short_label || g.title}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="obj-success">Definition of success</Label>
            <textarea
              id="obj-success"
              value={definition}
              onChange={(e) => setDefinition(e.target.value)}
              placeholder="What does “done” look like for this objective?"
              className="min-h-[80px] w-full resize-y rounded-xl border border-input bg-background px-3 py-2 text-sm"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="obj-plan-url">Plan link (optional)</Label>
            <Input
              id="obj-plan-url"
              value={planUrl}
              onChange={(e) => setPlanUrl(e.target.value)}
              placeholder="https://… (SharePoint, Confluence, Drive)"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Plan notes (optional)</Label>
            <RichTextEditor value={planBody} onChange={setPlanBody} placeholder="Outline the plan…" />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={submit} disabled={pending || !title.trim()}>
            {pending ? "Saving…" : isEdit ? "Save changes" : "Create objective"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
